import React, { useContext } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { InfoOutlined, TrendingUp } from '@mui/icons-material';
import {
    Grid,
    Card,
    CardContent,
    Typography,
    CardMedia,
    Paper,
    Skeleton,
    Box,
    Chip,
    Fade,
    Zoom
} from '@mui/material';
import { HomeContext } from '../../../Providers/HomeContext';

const {
    REACT_APP_URL_STORAGE,
} = process.env;

const useStyles = makeStyles((theme) => ({
    container: {
        backgroundColor: theme.palette.background.main,
        padding: '30px 20px',
        position: 'relative',
        overflow: 'hidden',
    },
    sectionTitle: {
        fontWeight: 800,
        fontSize: '42px',
        marginBottom: '5px',
        background: '#000',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
        [theme.breakpoints.down('md')]: {
            fontSize: '32px',
        },
    },
    sectionSubtitle: {
        fontSize: '18px',
        color: theme.palette.text.secondary,
        maxWidth: '600px',
        margin: '0 auto 30px',
    },
    cardContainer: {
        borderRadius: '24px',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        '&:hover': {
            transform: 'translateY(-12px)',
            boxShadow: `0 20px 40px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.15)'}`,
        },
        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            opacity: 0,
            transition: 'opacity 0.3s ease',
        },
        '&:hover::before': {
            opacity: 1,
        },
    },
    card: {
        borderRadius: '24px',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: theme.palette.background.paper,
        border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
    },
    cardMedia: {
        height: 240,
        position: 'relative',
        overflow: 'hidden',
        '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.1) 100%)',
        },
    },
    cardContent: {
        padding: '32px 24px',
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
    },
    courseTitle: {
        fontWeight: 800,
        fontSize: '28px',
        marginBottom: '20px',
        background: '#097200',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
        lineHeight: 1.3,
        letterSpacing: '-0.5px',
        transition: 'all 0.3s ease',
        position: 'relative',
        paddingBottom: '12px',
        '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '60px',
            height: '3px',
            background: theme.palette.letters.main,
            borderRadius: '2px',
            transition: 'width 0.3s ease',
        },
    },
    priceContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '24px',
        padding: '12px 20px',
        borderRadius: '16px',
        background: `linear-gradient(135deg, ${theme.palette.letters.main}15, ${theme.palette.letters.main}15)`,
        border: `2px solid ${theme.palette.letters.main}30`,
        width: 'fit-content',
    },
    price: {
        fontWeight: 800,
        fontSize: '32px',
        color: theme.palette.letters.main,
        lineHeight: 1,
    },
    priceLabel: {
        fontSize: '14px',
        color: theme.palette.text.secondary,
        fontWeight: 500,
    },
    featuresContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
    },
    featureChip: {
        padding: '12px 16px',
        borderRadius: '12px',
        backgroundColor: theme.palette.secondary.main,
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        transition: 'all 0.2s ease',
        '&:hover': {
            transform: 'translateX(4px)',
            backgroundColor: theme.palette.secondary.dark,
        },
    },
    featureIcon: {
        fontSize: '20px',
        color: theme.palette.letters.main,
        flexShrink: 0,
    },
    featureText: {
        fontSize: '15px',
        color: theme.palette.letters.main,
        fontWeight: 500,
        lineHeight: 1.4,
    },
    skeleton: {
        borderRadius: '24px',
        width: '100%',
        height: 500,
        [theme.breakpoints.down('md')]: {
            height: 400,
        },
    },
    badge: {
        position: 'absolute',
        top: 16,
        right: 16,
        zIndex: 1,
        background: theme.palette.secondary.main,
        color: theme.palette.letters.main,
        padding: '6px 14px',
        borderRadius: '20px',
        fontWeight: 600,
        fontSize: '12px',
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    },
}));

const CoursesV = () => {
    const { courses } = useContext(HomeContext);
    const classes = useStyles();

    // Función para formatear el precio con separador de miles
    const formatPrice = (price) => {
        return price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    };

    return (
        <Box className={classes.container} id='curses'>
            <Fade in timeout={800}>
                <Box textAlign="center" >
                    <Typography className={classes.sectionTitle}>
                        Nuestros Cursos
                    </Typography>
                    <Typography className={classes.sectionSubtitle}>
                        Descubri tu formación perfecta para aprender a manejar
                    </Typography>
                </Box>
            </Fade>

            <Grid container spacing={4} justifyContent="center">
                {courses ? (
                    courses.map((item, index) => (
                        <Grid item key={index} xs={12} sm={6} md={4} lg={4}>
                            <Zoom in timeout={400 + index * 100}>
                                <Paper className={classes.cardContainer} elevation={0}>
                                    <Card className={classes.card} elevation={0}>
                                        <Box position="relative">
                                            {/* <Box className={classes.badge}>
                                                <TrendingUp sx={{ fontSize: 16 }} />
                                                Popular
                                            </Box> */}
                                            <CardMedia
                                                className={classes.cardMedia}
                                                image={REACT_APP_URL_STORAGE + item.urlimg}
                                                alt={item.title}
                                            />
                                        </Box>

                                        <CardContent className={classes.cardContent}>
                                            <Typography className={classes.courseTitle}>
                                                {item.title}
                                            </Typography>

                                            <Box className={classes.priceContainer}>
                                                <Box>
                                                    <Typography className={classes.priceLabel}>
                                                        Precio
                                                    </Typography>
                                                    <Typography className={classes.price}>
                                                        ${formatPrice(item.price)}
                                                    </Typography>
                                                </Box>
                                            </Box>

                                            <Box className={classes.featuresContainer}>
                                                {item.features.map((feature, idx) => (
                                                    <Box
                                                        key={idx}
                                                        className={classes.featureChip}
                                                    >
                                                        <InfoOutlined className={classes.featureIcon} />
                                                        <Typography className={classes.featureText}>
                                                            {feature.label}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Paper>
                            </Zoom>
                        </Grid>
                    ))
                ) : (
                    <Grid item container spacing={4} justifyContent="center">
                        {[1, 2, 3].map((item) => (
                            <Grid item key={item} xs={12} sm={6} md={4}>
                                <Skeleton
                                    variant="rectangular"
                                    animation="wave"
                                    className={classes.skeleton}
                                />
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Grid>
        </Box>
    );
};

export default CoursesV;